import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { objectMapValues } from "@formant/core/common/objectMapValues";
import { throttle } from "@formant/core/common/throttle";
import { duration } from "@formant/core/model/duration";
import { ITags } from "@formant/core/model/ITags";
import { ITagSets } from "@formant/core/model/ITagSets";
import { IconName } from "@formant/ui/component/common/Icon";

import { SearchField } from "../SearchField";
import { MultiSelect } from "./MultiSelect";
import * as styles from "./SimpleFilter.scss";

export interface ISimpleFilterProps<T> {
    dimensions: ISimpleFilterDimension<T>[];
    rows: T[];
    filter: ISimpleFilterState;
    className?: string;
    placeholder?: string;
    onChangeFilter: (_: ISimpleFilterState) => void;
}

export interface ISimpleFilterDimension<T, U = any> {
    dimensionKey: string;
    dimensionName: string;
    getValue(row: T): U;
    getValueLabel?(value: U): string;
    getValueIcon?(value: U): IconName | undefined;
}

export interface ISimpleFilterState {
    search: string;
    dimensions: { [dimensionName: string]: any[] };
}

export const emptySimpleFilterState = {
    search: "",
    dimensions: {},
};

@observer
export class SimpleFilter<T> extends Component<ISimpleFilterProps<T>> {
    @observable
    private search: string = this.props.filter.search;

    private throttledUpdateSearch = throttle((search: string) => {
        const { filter, onChangeFilter } = this.props;
        onChangeFilter({ ...filter, search });
    }, 0.5 * duration.second);

    public render() {
        const { search, onChangeSearchField, onMultiSelectChange } = this;
        const { dimensions, rows, filter, className, placeholder } = this.props;

        if (rows.length < 2) {
            return false;
        }

        const uniqueValues = getUniqueValues(rows, dimensions);

        return (
            <div className={classnames(styles["simple-filter"], className)}>
                <SearchField
                    value={search}
                    onChange={onChangeSearchField}
                    className={styles.search}
                    placeholder={placeholder}
                />
                {dimensions.length > 0 && (
                    <div className={styles.dimensions}>
                        {dimensions.map(
                            ({
                                dimensionKey,
                                dimensionName,
                                getValueLabel,
                                getValueIcon,
                            }) => (
                                <MultiSelect
                                    key={dimensionKey}
                                    value={
                                        filter.dimensions[dimensionKey] || []
                                    }
                                    options={
                                        uniqueValues[dimensionKey]?.map(_ => ({
                                            value: _,
                                            label: getValueLabel?.(_) || _,
                                            icon: getValueIcon?.(_),
                                        })) || []
                                    }
                                    title={dimensionName}
                                    onChange={onMultiSelectChange}
                                    context={dimensionKey}
                                />
                            ),
                        )}
                    </div>
                )}
            </div>
        );
    }

    private onChangeSearchField = (query: string) => {
        this.search = query;
        this.throttledUpdateSearch(query);
    };

    private onMultiSelectChange = (selected: any[], dimensionKey?: string) => {
        const { filter, onChangeFilter } = this.props;
        onChangeFilter({
            ...filter,
            dimensions: {
                ...filter.dimensions,
                [defined(dimensionKey)]: selected,
            },
        });
    };
}

function getUniqueValues<T>(
    rows: T[],
    dimensions: ISimpleFilterDimension<T>[],
): { [dimensionName: string]: any[] } {
    const result: { [dimensionName: string]: Set<any> } = {};
    for (const row of rows) {
        for (const { dimensionKey, getValue } of dimensions) {
            const value = getValue(row);
            if (value !== undefined) {
                if (result[dimensionKey] === undefined) {
                    result[dimensionKey] = new Set<any>();
                }
                result[dimensionKey].add(value);
            }
        }
    }
    return objectMapValues(result, _ => {
        const a = Array.from(_);
        a.sort();
        return a;
    });
}

export function applySimpleFilter<T>(
    rows: T[],
    dimensions: ISimpleFilterDimension<T>[],
    getText: (row: T) => string,
    filter: ISimpleFilterState,
): T[] {
    return rows.filter(row => {
        // Every dimension in the filter must have at least 1 matching value in the row
        if (
            !dimensions.every(({ dimensionKey, getValue }) => {
                const dimension = filter.dimensions[dimensionKey];
                return (
                    !dimension ||
                    !dimension.length ||
                    dimension.some(_ => _ === getValue(row))
                );
            })
        ) {
            return false;
        }
        // Text filter must match the row
        if (filter.search) {
            const rowAsText = getText(row).toLocaleLowerCase();
            const searchInput = filter.search.toLocaleLowerCase();
            if (rowAsText.indexOf(searchInput) === -1) {
                return false;
            }
        }
        return true;
    });
}

export function tagDimensions<T>(
    rows: T[],
    getTags: (row: T) => ITags,
): ISimpleFilterDimension<T>[] {
    const tags: ITagSets = {};
    for (const row of rows) {
        const rowTags = getTags(row);
        for (const tag of Object.keys(rowTags)) {
            if (!tags[tag]) {
                tags[tag] = [];
            }
            for (const value of rowTags[tag] || []) {
                if (value && !tags[tag].includes(value)) {
                    tags[tag].push(value);
                }
            }
        }
    }
    return Object.keys(tags)
        .filter(_ => !_.startsWith("$."))
        .sort()
        .map(tag => ({
            dimensionKey: `tag-${tag}`, // Prevent tags from conflicting with hard-coded dimensions
            dimensionName: tag,
            getValue: (row: T) => getTags(row)[tag],
            getValueIcon: () => "tags",
        }));
}
